<script setup>
/**
 * AgentsPerformanceWidget
 * TOP 3 horizontal widget showing the top 3 performing agents.
 * Clicks emit strip-click for parent to handle.
 */
import { ref, computed, onMounted } from 'vue'
import { useStatistics } from '@core/composables/useStatistics.js'
import { getCollection } from '@core/firebase/firestore.js'

const MEDALS = ['🥇', '🥈', '🥉']

const currentMonthLabel = new Date().toLocaleDateString('he-IL', {
  month: 'long',
  year: 'numeric',
})

const emit = defineEmits(['strip-click'])
const { getAllAgentsAnalyses } = useStatistics()

const isLoading = ref(true)
const topAgents = ref([])
const allAgents = ref([])

onMounted(async () => {
  try {
    const [data, users] = await Promise.all([
      getAllAgentsAnalyses(),
      getCollection('Users').catch(() => []),
    ])

    const photoByAgent = {}
    for (const u of users) {
      const agentNum = u.agent || u.agent_number
      if (agentNum && u.photoURL) {
        photoByAgent[String(agentNum)] = u.photoURL
      }
    }

    allAgents.value = (data || [])
      .filter((a) => a.analys > 0)
      .sort((a, b) => b.analys - a.analys)
      .map((a, idx) => ({
        ...a,
        photoURL: photoByAgent[String(a.id)] || null,
        initials: a.name ? a.name.charAt(0) : '?',
        rank: idx < 3 ? idx + 1 : 0,
      }))

    topAgents.value = allAgents.value.slice(0, 3)
  } catch {
    /* silent – widget is non-critical */
  } finally {
    isLoading.value = false
  }
})

// Podium order: 2nd, 1st, 3rd (center spotlight)
const podiumAgents = computed(() => {
  const t = topAgents.value
  if (t.length < 3) return t
  return [t[1], t[0], t[2]]
})

function handleClick() {
  emit('strip-click', allAgents.value)
}
</script>

<template>
  <div class="top3" @click="handleClick">
    <span class="top3__month-label">{{ currentMonthLabel }}</span>

    <!-- Loading -->
    <div v-if="isLoading" class="top3__loading">
      <div class="top3__spinner" />
    </div>

    <!-- Top 3 agents -->
    <template v-else-if="topAgents.length">
      <span class="top3__title">TOP 3</span>
      <div class="top3__row">
        <div
          v-for="agent in podiumAgents"
          :key="agent.id"
          class="top3__card"
          :class="{ 'top3__card--first': agent.rank === 1 }"
        >
          <span class="top3__medal">{{ MEDALS[agent.rank - 1] }}</span>
          <div
            class="top3__avatar"
            :class="{
              'top3__avatar--gold': agent.rank === 1,
              'top3__avatar--silver': agent.rank === 2,
              'top3__avatar--bronze': agent.rank === 3,
            }"
          >
            <img
              v-if="agent.photoURL"
              :src="agent.photoURL"
              :alt="agent.name"
              class="top3__avatar-img"
            />
            <span v-else class="top3__avatar-fallback">{{ agent.initials }}</span>
          </div>
          <span class="top3__name">{{ agent.name }}</span>
          <span class="top3__pct">{{ agent.analys.toFixed(0) }}%</span>
        </div>
      </div>
    </template>

    <!-- No data -->
    <div v-else class="top3__empty">
      <span>אין נתונים</span>
    </div>
  </div>
</template>

<style scoped>
.top3 {
  position: relative;
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
  overflow: hidden;
  padding: var(--spacing-sm) var(--spacing-md);
  direction: rtl;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.top3__month-label {
  position: absolute;
  top: var(--spacing-sm);
  inset-inline-end: var(--spacing-md);
  font-size: 11px;
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);
  line-height: 1;
}

.top3__title {
  display: block;
  text-align: center;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--text-secondary);
  letter-spacing: 0.05em;
  margin-bottom: var(--spacing-xs);
}

.top3__loading,
.top3__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.top3__spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: top3-spin 0.8s linear infinite;
}

@keyframes top3-spin {
  to {
    transform: rotate(360deg);
  }
}

.top3__row {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: var(--spacing-sm);
}

.top3__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex: 1;
  max-width: 110px;
  padding: var(--spacing-xs);
  border-radius: var(--border-radius-md);
  transition: background var(--transition-fast);
}

.top3__card--first {
  transform: scale(1.12);
}

.top3__medal {
  font-size: 16px;
  line-height: 1;
}

.top3__card--first .top3__medal {
  font-size: 20px;
}

.top3__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  flex-shrink: 0;
}

.top3__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.top3__avatar-fallback {
  color: #fff;
  font-size: 15px;
  font-weight: var(--font-weight-bold);
  line-height: 1;
}

.top3__name {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  line-height: 1.2;
  max-width: 90px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top3__pct {
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  line-height: 1;
}

/* Podium borders */
.top3__avatar--gold {
  border: 3px solid transparent;
  background:
    linear-gradient(135deg, #1e293b, #334155) padding-box,
    linear-gradient(135deg, #f9d423, #f8b500, #fce38a, #f8b500) border-box;
}

.top3__avatar--silver {
  border: 3px solid transparent;
  background:
    linear-gradient(135deg, #1e293b, #334155) padding-box,
    linear-gradient(135deg, #e8e8e8, #b0b0b0, #d8d8d8, #c0c0c0) border-box;
}

.top3__avatar--bronze {
  border: 3px solid #cd7f32;
}

@media (max-width: 768px) {
  .top3 {
    padding: var(--spacing-xs) var(--spacing-sm);
  }

  .top3__row {
    gap: var(--spacing-xs);
  }

  .top3__card {
    padding: var(--spacing-xs);
  }

  .top3__avatar {
    width: 34px;
    height: 34px;
  }

  .top3__avatar-fallback {
    font-size: 14px;
  }

  .top3__medal {
    font-size: 14px;
  }
}
</style>
